import { EnvironmentInjector, Injectable, runInInjectionContext, signal } from '@angular/core';
import {
	addDoc,
	arrayUnion,
	collection,
	CollectionReference,
	collectionSnapshots,
	deleteDoc,
	doc,
	docData,
	DocumentData,
	DocumentReference,
	Firestore,
	Query,
	getDoc,
	getDocs,
	limit,
	orderBy,
	query,
	setDoc,
	updateDoc,
	where,
} from '@angular/fire/firestore';
import { BehaviorSubject, map, Observable } from 'rxjs';
import { MAS_Accounts_Service, MAS_Schedules_Service } from '.';
import { MasProgramConverterService } from '../converters';
import { chunkForFirestoreIn } from '@common/domain/firestore-in-query';
import { Lesson, MASProgramAndSchedulesModel, MASProgramModel, Program, ProgramMember, SkillGroup } from '../models';
import { AdvancedDebugging, formatMasLocalDateTime, ScheduleProcessor } from '../utilities';

@Injectable({
	providedIn: 'root',
})
export class MAS_Programs_Service {
	public sharedData = signal<{ name: string; age: number }>({ name: 'John Doe', age: 30 });
	private _isLegacy$ = new BehaviorSubject<boolean>(false);
	public isLegacy$ = this._isLegacy$.asObservable();
	private _accountsService?: MAS_Accounts_Service;
	private advancedDebugging = new AdvancedDebugging();

	constructor(public afs: Firestore, private schedule_Service: MAS_Schedules_Service, private programConverter: MasProgramConverterService, private injector: EnvironmentInjector) {}

	public get accountsService(): MAS_Accounts_Service {
		if (!this._accountsService) {
			this._accountsService = this.injector.get(MAS_Accounts_Service);
		}
		return this._accountsService;
	}
	getSharedData(value: { name: string; age: number }) {
		this.sharedData.set(value);
	}

	private failPrograms(operation: string, error: unknown): never {
		this.advancedDebugging.captureError(operation, error, { tags: { operation } });
		throw error;
	}

	async getLegacyLessons(): Promise<Lesson[]> {
		try {
			const q = query<Lesson, DocumentData>(collection(this.afs, 'mas-lessons') as CollectionReference<Lesson>);
			const qDocs = await getDocs(q);
			if (!qDocs) return [];
			return qDocs.docs.map(m => this.convertLessonDatesFromFirestore(m.data()));
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.getLegacyLessons', error);
		}
	}

	async checkYASMembership(id: string) {
		try {
			const q = query(collection(this.afs, 'mas-programs'), where('name', '==', 'YAS'));
			const qDocs = await getDocs(q);
			if (!qDocs) return false;
			const yasProgram = <Program>qDocs.docs.pop()?.data();
			if (!yasProgram) return false;
			const yasMembership = yasProgram.memberships?.map(m => m.memberId);
			return yasMembership?.includes(id);
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.checkYASMembership', error);
		}
	}

	async checkIfLegacy(memberships: string[]): Promise<void> {
		try {
			const programs = await this.getProgramsByMemberships(memberships);
			const isLegacy = programs.some(program => (program?.name ?? '').trim().toLowerCase() === 'legacy');
			this._isLegacy$.next(isLegacy);
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.checkIfLegacy', error);
		}
	}

	/**
	 * @deprecated Use getProgramModelById instead.
	 */
	async getProgramById(programId: string): Promise<Program | undefined> {
		try {
			const docRef = doc(this.afs, 'mas-programs', programId);
			const qDoc = await getDoc(docRef);
			if (!qDoc) return;
			return qDoc.data() as Program;
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.getProgramById', error);
		}
	}

	async getProgramModelById(programId: string): Promise<MASProgramModel | undefined> {
		try {
			const docRef = doc(this.afs, 'mas-programs', programId);
			const qDoc = await getDoc(docRef);
			if (!qDoc.exists()) return;

			const rawProgram = qDoc.data() as Program;
			return this.programConverter.fromRaw(rawProgram);
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.getProgramModelById', error);
		}
	}

	async getProgramByName(programName: string): Promise<Program | undefined> {
		try {
			const q = query<Program, DocumentData>(collection(this.afs, 'mas-programs') as CollectionReference<Program>, where('name', '==', programName));
			const qDocs = await getDocs(q);
			if (!qDocs) return;
			return qDocs.docs.map(m => m.data()).pop() as Program;
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.getProgramByName', error);
		}
	}

	/**
	 *! @deprecated Use getProgramModelsAsObservable instead.
	 */
	getProgramsAsObservable(): Observable<Program[]> {
		return collectionSnapshots<Program>(
			query<Program, DocumentData>(collection(this.afs, 'mas-programs') as CollectionReference<Program>, where('status', '==', 'Active'), orderBy('status'), orderBy('name'))
		).pipe(
			map(changes => {
				return changes.map(a => {
					const data = a.data();
					data.id = a.id;
					return data;
				});
			})
		);
	}

	getProgramModelsAsObservable(): Observable<MASProgramModel[]> {
		return collectionSnapshots<Program>(
			query<Program, DocumentData>(collection(this.afs, 'mas-programs') as CollectionReference<Program>, where('status', '==', 'Active'), orderBy('status'), orderBy('name'))
		).pipe(
			map(changes =>
				changes.map(a => {
					const data = a.data();
					data.id = a.id;

					return this.programConverter.fromRaw(data);
				})
			)
		);
	}
	/**
	 * @deprecated Use getProgramModelsAsPromise instead.
	 */
	async getProgramsAsPromise(): Promise<Program[]> {
		try {
			const q = query<Program, DocumentData>(collection(this.afs, 'mas-programs') as CollectionReference<Program>, where('status', '==', 'Active'));
			const qDocs = await getDocs(q);
			if (!qDocs) return [];
			return qDocs.docs.map(m => m.data());
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.getProgramsAsPromise', error);
		}
	}

	async getProgramModelsAsPromise(): Promise<MASProgramModel[]> {
		try {
			return await runInInjectionContext(this.injector, async () => {
				const q = query<Program, DocumentData>(collection(this.afs, 'mas-programs') as CollectionReference<Program>, where('status', '==', 'Active'));
				const qDocs = await getDocs(q);
				if (!qDocs) return [];
				return qDocs.docs.map(doc => this.programConverter.fromRaw(doc.data()));
			});
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.getProgramModelsAsPromise', error);
		}
	}

	/**
	 * ! @deprecated Use getProgramModelsByMemberships instead.
	 */
	async getProgramsByMemberships(memberships: string[]): Promise<Program[]> {
		try {
			if (!memberships || memberships.length === 0) {
				return [];
			}
			const programsCollection = collection(this.afs, 'mas-programs') as CollectionReference<Program>;
			const rows = await this.queryProgramDocsInBatches(memberships, batch =>
				query<Program, DocumentData>(programsCollection, where('status', '==', 'Active'), where('id', 'in', batch), orderBy('name')),
			);
			rows.sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')));
			return rows;
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.getProgramsByMemberships', error);
		}
	}

	async getProgramModelsByMemberships(memberships: string[]): Promise<MASProgramModel[]> {
		try {
			const programs = await this.getProgramsByMemberships(memberships);
			return programs.map(row => this.programConverter.fromRaw(row));
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.getProgramModelsByMemberships', error);
		}
	}
	async getTestCategory(memberships: string[]): Promise<any> {
		try {
			if (!memberships || memberships.length === 0) {
				return [];
			}
			const coll = collection(this.afs, 'mas-programs') as CollectionReference<Program>;
			const rows = await this.queryProgramDocsInBatches(memberships, batch =>
				query<Program, DocumentData>(coll, where('status', '==', 'Active'), where('id', 'in', batch)),
			);
			const top = rows.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))[0];
			return top?.testCategory != null ? [top.testCategory] : [];
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.getTestCategory', error);
		}
	}

	async getPrimaryCategory(programs: string[]): Promise<string> {
		try {
			if (!programs || programs.length === 0) {
				return '';
			}
			const coll = collection(this.afs, 'mas-programs') as CollectionReference<Program>;
			const rows = await this.queryProgramDocsInBatches(programs, batch =>
				query<Program, DocumentData>(coll, where('id', 'in', batch), where('optionReserve', '==', true)),
			);
			const top = rows.sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))[0];
			return top?.testCategory ?? '';
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.getPrimaryCategory', error);
		}
	}

	async getProgramsWithReservations(): Promise<Program[]> {
		try {
			const q = query<Program, DocumentData>(
				collection(this.afs, 'mas-programs') as CollectionReference<Program>,
				where('status', '==', 'Active'),
				where('optionReserve', '==', true),
				orderBy('name')
			);
			const qDocs = await getDocs(q);
			if (!qDocs) return [];
			return qDocs.docs.map(m => m.data());
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.getProgramsWithReservations', error);
		}
	}

	async getProgramsAndSchedules(programsList: string[]): Promise<MASProgramAndSchedulesModel[]> {
		try {
			if (!programsList || programsList.length === 0) return [];

			const coll = collection(this.afs, 'mas-programs') as CollectionReference<Program>;
			const rows = await this.queryProgramDocsInBatches(programsList, batch =>
				query<Program, DocumentData>(coll, where('id', 'in', batch), where('optionReserve', '==', true), orderBy('name')),
			);
			rows.sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')));

			const programs = rows.map(async rawProgram => {
				const programModel = new MASProgramModel(rawProgram, this);
				return new MASProgramAndSchedulesModel(programModel, [], undefined, null as any);
			});

			return await Promise.all(programs);
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.getProgramsAndSchedules', error);
		}
	}

	async getProgramsAndSchedulesV2(programsList: string[]): Promise<MASProgramAndSchedulesModel[]> {
		try {
			if (!programsList || programsList.length === 0) return [];
			const coll = collection(this.afs, 'mas-programs') as CollectionReference<Program>;
			const rows = await this.queryProgramDocsInBatches(programsList, batch =>
				query<Program, DocumentData>(coll, where('id', 'in', batch), where('optionReserve', '==', true), orderBy('name')),
			);
			rows.sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')));
			const qData = rows.map(row => this.programConverter.fromRaw(row));

			if (!qData.length) return [];

			const programsWithSchedules = qData.map(async (program): Promise<any> => {
				const schedules = await this.schedule_Service.getFutureScheduleByProgramName(program.name);

				return { ...program, schedules };
			});

			return Promise.all(programsWithSchedules);
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.getProgramsAndSchedulesV2', error);
		}
	}

	async getProgramsByMembershipProperties(properties: ProgramMember): Promise<Program[]> {
		try {
			const q = query<Program, DocumentData>(collection(this.afs, 'mas-programs') as CollectionReference<Program>, where('memberships', 'array-contains', properties));
			const qDocs = await getDocs(q);
			if (!qDocs) return [];
			return qDocs.docs.map(m => m.data());
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.getProgramsByMembershipProperties', error);
		}
	}

	async getProgramsWithTestCategory(): Promise<Program[]> {
		try {
			const q = query<Program, DocumentData>(collection(this.afs, 'mas-programs') as CollectionReference<Program>, where('status', '==', 'Active'));
			const qDocs = await getDocs(q);
			if (!qDocs) return [];
			const docs = qDocs.docs.map(m => m.data());
			const fdocs = docs.filter(f => f.testCategory !== undefined);
			return fdocs;
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.getProgramsWithTestCategory', error);
		}
	}

	async getProgramByTestCategory(category: string): Promise<Program | undefined> {
		try {
			const q = query<Program, DocumentData>(collection(this.afs, 'mas-programs') as CollectionReference<Program>, where('status', '==', 'Active'), where('testCategory', '==', category));
			const qDocs = await getDocs(q);
			if (!qDocs) return;
			return qDocs.docs.map(m => m.data()).pop() as Program;
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.getProgramByTestCategory', error);
		}
	}

	async setProgram(id: string, payload: any) {
		try {
			if (payload.css) delete payload.css;
			const ref = doc(this.afs, 'mas-programs', id);
			await setDoc(ref, payload, { merge: true });
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.setProgram', error);
		}
	}

	async createLesson(payload: Lesson): Promise<DocumentReference<DocumentData>> {
		try {
			const ref = collection(this.afs, 'mas-lessons');
			return await addDoc(ref, payload).then(doc => doc);
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.createLesson', error);
		}
	}

	async deleteLesson(id: string) {
		try {
			const ref = doc(this.afs, 'mas-lessons', id);
			return await deleteDoc(ref).then(doc => doc);
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.deleteLesson', error);
		}
	}

	getSkills(): Observable<SkillGroup[]> {
		const docRef = doc(this.afs, 'mas-parameters', 'lists');
		return docData(docRef).pipe(
			map(data => {
				if (data && data['skills']) {
					return data['skills'] as SkillGroup[];
				}
				return [];
			})
		);
	}

	async updateSkills(skills: SkillGroup[]): Promise<void> {
		try {
			const docRef = doc(this.afs, 'mas-parameters', 'lists');
			const docSnap = await getDoc(docRef);
			if (!docSnap.exists()) {
				await setDoc(docRef, { skills });
			} else {
				await updateDoc(docRef, { skills });
			}
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.updateSkills', error);
		}
	}

	private convertLessonDatesFromFirestore(lesson: Lesson): Lesson {
		if (lesson.lessonTime && typeof lesson.lessonTime === 'string') {
			const dt = new Date(lesson.lessonTime);
			if (!isNaN(dt.getTime())) {
				lesson.lessonTime = dt;
			}
		}
		return lesson;
	}

	getLegacyLessonsAsObservable(): Observable<Lesson[]> {
		return collectionSnapshots<Lesson>(query<Lesson, DocumentData>(collection(this.afs, 'mas-lessons') as CollectionReference<Lesson>, where('reserved', '==', false))).pipe(
			map(changes => {
				return changes.map(a => {
					const data = a.data();
					data.id = a.id;
					return this.convertLessonDatesFromFirestore(data);
				});
			})
		);
	}

	async setLesson(id: string, payload: Partial<Lesson>) {
		try {
			const ref = doc(this.afs, 'mas-lessons', id);
			if (payload.lessonTime instanceof Date) {
				payload.lessonTime = formatMasLocalDateTime(payload.lessonTime) as any;
			}
			await setDoc(ref, payload, { merge: false });
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.setLesson', error);
		}
	}

	async addLessonAcknowledgement(id: string, accountId: string): Promise<void> {
		try {
			const ref = doc(this.afs, 'mas-lessons', id);
			await updateDoc(ref, {
				acknowledgements: arrayUnion({ accountId }),
			});
		} catch (error) {
			this.failPrograms('mas:MAS_Programs_Service.addLessonAcknowledgement', error);
		}
	}

	async updateScheduleWithProgramId(schedules: any[]): Promise<any[]> {
		const programs = await this.getProgramsAsPromise();
		return new ScheduleProcessor(schedules).addProgramIds(programs).getEvents();
	}

	/** Runs the same query for each Firestore-safe `in` batch and merges by program id. */
	private async queryProgramDocsInBatches(
		values: string[],
		buildQuery: (batch: string[]) => Query<Program, DocumentData>,
	): Promise<Program[]> {
		const byId = new Map<string, Program>();
		for (const batch of chunkForFirestoreIn(values)) {
			const snap = await getDocs(buildQuery(batch));
			for (const docSnap of snap.docs) {
				const data = docSnap.data();
				const id = data.id ?? docSnap.id;
				if (!byId.has(id)) {
					byId.set(id, data);
				}
			}
		}
		return [...byId.values()];
	}
}
